import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ProjectDetailCarousel.css';

const resolveAsset = (projectId, filename) => {
  if (!filename) return null;
  return `/assets/projects/${projectId}/${filename}`;
};

export default function ProjectDetailCarousel() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [index, setIndex] = useState(0);
  const viewportRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    fetch('/data/portfolio/projects.json')
      .then((r) => r.json())
      .then((projects) => {
        const project = projects.find((p) => p.slug === slug);
        if (!project) { setNotFound(true); return null; }
        return fetch(`/data/portfolio/${project.id}.json`);
      })
      .then((r) => r && r.json())
      .then((data) => { if (data) setMeta(data); })
      .catch((e) => { console.error('Failed to load project', e); setNotFound(true); });
  }, [slug]);

  const slides = useMemo(() => {
    if (!meta) return [];

    const images = meta.images || [];

    // Cover slide
    const cover = resolveAsset(meta.id, '01-cover.jpg');

    // Intro images from text_image field
    const textImgNames = Array.isArray(meta.text_image)
      ? meta.text_image
      : meta.text_image ? [meta.text_image] : [];

    const introImages = textImgNames
      .filter((name) => images.includes(name))
      .map((name) => resolveAsset(meta.id, name));

    const arr = [];
    if (cover) arr.push({ type: 'cover', src: cover, key: 'cover' });
    arr.push({ type: 'intro', imgs: introImages, key: 'intro' });

    // Use gallery from JSON if present (project 03 has mixed image/text slides)
    if (meta.gallery && Array.isArray(meta.gallery)) {
      const customSlides = meta.gallery.map((item, idx) => {
        if (typeof item === 'string') {
          return { type: 'image', src: resolveAsset(meta.id, item), key: `g-${idx}-${item}` };
        }
        if (item && item.type === 'text') {
          return { type: 'text', content: item.content, key: `g-${idx}-text` };
        }
        return null;
      }).filter(Boolean);
      return [...arr, ...customSlides];
    }

    // Auto-build gallery from images array
    const excludedLower = new Set(['01-cover.jpg', ...textImgNames.map((n) => n.toLowerCase())]);
    const galleryFiles = images.filter((name) => {
      if (excludedLower.has(name.toLowerCase())) return false;
      if (!meta.text_image && /^01(-title)?\./i.test(name)) return false;
      const m = name.match(/^(\d+)/);
      return m && Number(m[1]) >= 2;
    });

    const gallery = galleryFiles.map((name) => ({
      type: 'image',
      src: resolveAsset(meta.id, name),
      key: `img-${name}`
    }));

    return [...arr, ...gallery];
  }, [meta]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(slides.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length]);

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(slides.length - 1, i + 1));

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -40) next();
    else if (dx > 40) prev();
    touchStartX.current = null;
  };

  if (notFound) {
    return (
      <div className="proj-slider">
        <div className="proj-head">
          <Link to="/portfolio" className="back">← Zurück</Link>
          <div className="spacer" />
        </div>
        <div className="empty">Projekt nicht gefunden.</div>
      </div>
    );
  }

  if (!meta) return null;

  return (
    <div className="proj-slider">
      <div className="proj-head">
        <Link
          to="/#portfolio"
          state={{ skipSplash: true }}
          className="back-arrow"
          aria-label="Zurück"
        >
          <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </Link>
        <div className="pager">{slides.length ? index + 1 : 0} / {slides.length}</div>
      </div>

      <div
        className="proj-viewport"
        ref={viewportRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {slides.length > 0 && <Slide slide={slides[index]} meta={meta} />}

        <button className="nav prev" onClick={prev} aria-label="Vorherige" disabled={index === 0}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button className="nav next" onClick={next} aria-label="Nächste" disabled={index === slides.length - 1}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
    </div>
  );
}

/* Characters missing from mundial-narrow-variable — render in a matching thin fallback */
const MISSING_GLYPHS = /[ŠšČčŽžĆćĐđ]/g;
const FALLBACK_STYLE = { fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', fontWeight: 200 };

const renderTextWithLinks = (text) => {
  if (!text) return null;
  const tokenRegex = /(\[[^\]]+\]\(https?:\/\/[^)]+\)|https?:\/\/[^\s]+|www\.[^\s]+|[ŠšČčŽžĆćĐđ])/g;
  const parts = text.split(tokenRegex);
  return parts.map((part, i) => {
    const mdLink = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
    if (mdLink) {
      return <a key={i} href={mdLink[2]} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', color: 'inherit' }}>{mdLink[1]}</a>;
    }
    if (part.match(/^https?:\/\/|^www\./)) {
      const href = part.startsWith('www.') ? `https://${part}` : part;
      return <a key={i} href={href} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', color: 'inherit' }}>{part}</a>;
    }
    if (MISSING_GLYPHS.test(part)) {
      MISSING_GLYPHS.lastIndex = 0;
      return <span key={i} style={FALLBACK_STYLE}>{part}</span>;
    }
    return part;
  });
};

function Slide({ slide, meta }) {
  if (slide.type === 'cover') {
    return (
      <div className="slide slide-image">
        <img src={slide.src} alt="" />
      </div>
    );
  }

  if (slide.type === 'intro') {
    return (
      <div className="slide slide-intro">
        <div className="intro-wrap">
          <div className="intro-text">
            {meta.client && <div className="client-label">{meta.client}</div>}
            <h2 className="intro-title">{meta.title}</h2>
            {meta.subtitle && <h3 className="intro-subtitle">{meta.subtitle}</h3>}
            <ul className="facts">
              {meta.credits && (
                <>
                  {meta.credits.photography && <li><strong>Foto:</strong> {meta.credits.photography}</li>}
                  {meta.credits.website && <li><a href={`https://${meta.credits.website.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{meta.credits.website}</a></li>}
                  {meta.credits.graphic && <li><strong>Grafik:</strong> {meta.credits.graphic}</li>}
                  {meta.credits.copyright && <li><strong>©</strong> {meta.credits.copyright}</li>}
                  {meta.credits.text && <li>{meta.credits.text}</li>}
                  {meta.credits.product_photo && <li><strong>Produktfotos:</strong> {meta.credits.product_photo}</li>}
                  {meta.credits.paper && <li><strong>Papier:</strong> {meta.credits.paper}</li>}
                  {meta.credits.feature && <li><strong>Besonderheit:</strong> {meta.credits.feature}</li>}
                  {meta.credits.print && <li><strong>Druck:</strong> {meta.credits.print}</li>}
                </>
              )}
              {meta.year && <li><strong>Jahr:</strong> {meta.year}</li>}
              {Array.isArray(meta.roles) && meta.roles.length > 0 && (
                <li><strong>Rolle:</strong> {meta.roles.join(', ')}</li>
              )}
            </ul>
            {Array.isArray(meta.body) && meta.body.length > 0 && (
              <div className="body">
                {meta.body.map((t, i) => <p key={i}>{renderTextWithLinks(t)}</p>)}
              </div>
            )}
          </div>
          {slide.imgs && slide.imgs.length > 0 && (
            <div className={`intro-images count-${slide.imgs.length}`}>
              {slide.imgs.map((src, i) => (
                <div className="intro-image-item" key={i}>
                  <img src={src} alt="" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (slide.type === 'text') {
    return (
      <div className="slide slide-intro">
        <div className="intro-wrap">
          <div className="intro-text" style={{ flex: 1, maxWidth: '100%' }}>
            <div className="body" style={{ whiteSpace: 'pre-wrap' }}>
              {renderTextWithLinks(slide.content)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="slide slide-image">
      <img src={slide.src} alt="" />
    </div>
  );
}
